import React, { useMemo, useState, useRef, useCallback } from "react";
import moment from "moment";
import SoftBox from "../SoftBox";
import MetricSparkline from "../CompanyMetrics/MetricSparkline";
import DerivedTrendChart from "./DerivedTrendChart";
import Standing from "./Standing";
import Activity from "./Activity";
import LightSelect from "../../pages/CompanyView/LightSelect";
import { UseTopPlayers } from "../../hooks/kpi";
import { UseSelectedWorkspace, UseUsersDictionary } from "../../hooks/company";
import CardHeader, { CARD_ROOT, CARD_BODY_PAD } from "../CardHeader";
import { yetiStanding } from "./yetiPace";
import TileTip from "./TileTip";
import YetiTeams, { YetiOverall, YetiTotal } from "./YetiTeams";
import { curveGeometry, reachedTierIndex, sortedTiers, payoutWithGates, tierGateStates, gatePeriodLabel, tierLabel } from "./payoutCurve";
import GatePopover, { GateLine } from "./GatePopover";
import BonusLadder from "./BonusLadder";
import { sanitizeBonus, isStepBonus, bonusPillText } from "./bonusSteps";
import { resolveMetricColor } from "../../helpers/metricColor";
import { periodWindowLabel } from "../../helpers/periodWindowLabel";
import { displayTargetOf } from "../../helpers/kpiTargetScale";
import { isRateLikeKpi, formatKpiPercent } from "../../helpers/scoreAggregate";
import { companyToday } from "../../helpers/companyTime";
import { resolveWindow } from "../../pages/CompanyMetrics/dashboards/useWorkspaceData";
import "../shared/Loading/skeleton.css";
import "./customWidget.css";

/**
 * CustomWidget — the "Build your own" dashboard card.
 *
 * An admin names it, picks one of the workspace's REAL company metrics, and
 * picks a graph. On the board it shows THE VIEWING REP'S OWN value/trend for
 * that metric — the same graph palette the Company Metrics appearance designer
 * offers, drawn from real data:
 *   • value + target shapes (number, ring, donut, gauge, target bar) read the
 *     rep's current `amount` + `target` from `ctx.kpis`;
 *   • trend shapes (bar, line, area, sparkline) read the rep's OWN daily series,
 *     built in Homepage from GET_ALL_USER_KPIS filtered to the logged-in user
 *     (the exact functions the designer uses, fed one user instead of the whole
 *     company) and passed in as `seriesByDay`.
 *
 * Nothing is invented. A metric not live in the workspace yet shows an honest
 * "not available yet" state; a trend chart with no real series yet falls back to
 * the current number rather than drawing a fake flat line.
 */

const FONT = '"DM Sans", system-ui, sans-serif';
const SERIES_GRAPHS = ["bar", "line", "area", "spark"];

const metricIdEq = (a, b) => a != null && b != null && String(a) === String(b);

/* `meta` (the metric/KPI object) is optional — pass it whenever known so
   Quality Bid Accuracy can render its one-decimal form; every other percent
   keeps rounding to a whole number. */
const fmt = (kind, v, meta = null) => {
  if (v == null || Number.isNaN(Number(v))) return "—";
  const n = Number(v);
  if (kind === "$") return `$${Math.round(n).toLocaleString()}`;
  if (kind === "%") {
    /* Scorekeeper stores rates as 0–1 fractions (0.44 = 44%). */
    const pct = Math.abs(n) <= 1 ? n : n / 100;
    return formatKpiPercent(meta, pct);
  }
  return Math.round(n).toLocaleString();
};

const kindOf = (m) => {
  if (m && (m.kind === "$" || m.kind === "%")) return m.kind;
  const t = `${m?.title || m?.name || ""}`.toLowerCase();
  if (/\bclose[d]?\s*rate\b|\bconversion\b|\bwin\s*rate\b/.test(t)) return "%";
  return "#";
};
const pctLabel = (pct) => `${Math.round((pct || 0) * 100)}% of target`;

/* ── value + target primitives. `pct` is the true ratio (can exceed 1); each
   shape clamps only what it DRAWS, never the % it PRINTS. ─────────────────── */

export const Ring = ({ pct, color, center, sub }) => {
  const r = 46;
  const c = 2 * Math.PI * r;
  const draw = Math.max(0, Math.min(1, pct || 0));
  return (
    <div style={{ position: "relative", width: 118, height: 118 }}>
      <svg width="118" height="118" viewBox="0 0 118 118">
        <circle cx="59" cy="59" r={r} fill="none" stroke="var(--tvd-tile, #EEF1F4)" strokeWidth="11" />
        <circle cx="59" cy="59" r={r} fill="none" stroke={color} strokeWidth="11" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - draw)} transform="rotate(-90 59 59)" style={{ transition: "stroke-dashoffset 700ms ease" }} />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <span style={{ fontSize: 21, fontWeight: 600, color: "var(--tvd-ink, #101828)", lineHeight: 1 }}>{center}</span>
        {sub && <span style={{ fontSize: 10.5, color: "var(--tvd-muted, #98A2B3)", marginTop: 3 }}>{sub}</span>}
      </div>
    </div>
  );
};

export const Donut = ({ pct, color, center, sub }) => {
  const r = 40;
  const c = 2 * Math.PI * r;
  const draw = Math.max(0, Math.min(1, pct || 0));
  return (
    <div style={{ position: "relative", width: 118, height: 118 }}>
      <svg width="118" height="118" viewBox="0 0 118 118">
        <circle cx="59" cy="59" r={r} fill="none" stroke="var(--tvd-tile, #EEF1F4)" strokeWidth="26" />
        <circle cx="59" cy="59" r={r} fill="none" stroke={color} strokeWidth="26" strokeDasharray={`${c * draw} ${c}`} transform="rotate(-90 59 59)" style={{ transition: "stroke-dasharray 700ms ease" }} />
        <circle cx="59" cy="59" r={22} fill="var(--tvd-card, #fff)" />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <span style={{ fontSize: 18, fontWeight: 600, color: "var(--tvd-ink, #101828)", lineHeight: 1 }}>{center}</span>
        {sub && <span style={{ fontSize: 10, color: "var(--tvd-muted, #98A2B3)", marginTop: 2 }}>{sub}</span>}
      </div>
    </div>
  );
};

export const Gauge = ({ pct, color, center, sub }) => {
  const r = 50;
  const cx = 62;
  const cy = 60;
  const draw = Math.max(0, Math.min(1, pct || 0));
  const track = `M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`;
  const ang = Math.PI * (1 - draw);
  const ex = cx + r * Math.cos(ang);
  const ey = cy - r * Math.sin(ang);
  /* SVG's large-arc-flag picks the arc ≥180° between the two endpoints — but
     the progress arc's own sweep here never exceeds 180° (a gauge is a half
     circle, draw is clamped to [0,1] → 0°–180°), so this must always be the
     MINOR arc, never the major one. Reading it off "past halfway" instead of
     off the actual sweep meant anything over 50% drew the major (long way
     around, 360°−sweep) arc — well past a full loop back near its own start,
     overlapping the track and spilling outside the viewBox: the "broken
     ring" look. */
  const big = 0;
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 132 }}>
      <svg width="124" height="70" viewBox="0 0 124 70">
        <path d={track} fill="none" stroke="var(--tvd-tile, #EEF1F4)" strokeWidth="11" strokeLinecap="round" />
        <path d={`M ${cx - r} ${cy} A ${r} ${r} 0 ${big} 1 ${ex} ${ey}`} fill="none" stroke={color} strokeWidth="11" strokeLinecap="round" />
      </svg>
      <span style={{ fontSize: 21, fontWeight: 600, color: "var(--tvd-ink, #101828)", marginTop: -6 }}>{center}</span>
      {sub && <span style={{ fontSize: 10.5, color: "var(--tvd-muted, #98A2B3)", marginTop: 2 }}>{sub}</span>}
    </div>
  );
};

export const TargetBar = ({ pct, color, value, target, kind }) => {
  const draw = Math.max(0, Math.min(1, pct || 0));
  return (
    <div style={{ width: "100%", maxWidth: 340 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8 }}>
        <span style={{ fontSize: 27, fontWeight: 600, color: "var(--tvd-ink, #101828)" }}>{fmt(kind, value)}</span>
        <span style={{ fontSize: 11.5, color: "var(--tvd-muted, #98A2B3)" }}>{Math.round((pct || 0) * 100)}% of target</span>
      </div>
      <div style={{ position: "relative", height: 10, borderRadius: 999, background: "var(--tvd-tile, #EEF1F4)", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, width: `${draw * 100}%`, background: color, borderRadius: 999, transition: "width 700ms ease" }} />
      </div>
      {target > 0 && <div style={{ marginTop: 6, fontSize: 10.5, color: "var(--tvd-muted, #98A2B3)" }}>Target {fmt(kind, target)}</div>}
    </div>
  );
};

export const BigNumber = ({ value, kind, label }) => (
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
    <span style={{ fontSize: 42, fontWeight: 600, color: "var(--tvd-ink, #101828)", lineHeight: 1 }}>{fmt(kind, value)}</span>
    {label && <span style={{ fontSize: 12, color: "var(--tvd-muted, #98A2B3)" }}>{label}</span>}
  </div>
);

/* Vertical bars for the rep's own daily series — the same shape the appearance
   designer's "Bar" draws, sized to the card. */
export const MiniBars = ({ series, color }) => {
  const vals = (series || []).map((d) => Number(d?.value)).filter((v) => Number.isFinite(v)).slice(-16);
  const max = Math.max(...vals, 1);
  const gap = 3;
  const W = 300;
  const H = 96;
  const n = vals.length;
  const bw = (W - gap * (n - 1)) / n;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" width="100%" height={H} style={{ display: "block" }} aria-hidden>
      {vals.map((v, i) => {
        const h = Math.max(2, (Math.sqrt(Math.max(0, v)) / Math.sqrt(max)) * (H - 6));
        // eslint-disable-next-line react/no-array-index-key -- bars are positional; index is the identity
        return <rect key={i} x={i * (bw + gap)} y={H - h} width={bw} height={h} rx={2} fill={color} fillOpacity="0.85" />;
      })}
    </svg>
  );
};

/* ── the card ────────────────────────────────────────────────────────────── */

/* Where the goal sits along a goal bar that marks it (see MetricBlock): the
   track is 1 / 0.8 = 125% of the goal long. */
const GOAL_MARK_AT = 80;

/* One metric row: resolves its live value and draws it in the graph chosen for
   it. Used for every metric in the widget, whatever the layout. */
const MetricBlock = ({ kpis, seriesByDay, row, primary, accent, big, periodLabel = "this month", period = "month", customRange = null, loading = false }) => {
  /* A stable-per-instance suffix so two cards showing the same derived
     metric at once don't collide on the SVG gradient's DOM id. */
  const chartInstanceId = useMemo(() => Math.random().toString(36).slice(2), []);
  const metric = useMemo(() => {
    const list = kpis || [];
    const hit = list.find((k) => k && metricIdEq(k._id, row.metricId));
    if (hit) return hit;
    /* Scoped pairing remaps ids; if this card has exactly one kpi, use it. */
    if (list.length === 1 && row.metricId) return list[0];
    return null;
  }, [kpis, row.metricId]);
  const kind = kindOf(metric);
  const label = (metric?.title || metric?.name || "").trim();
  const value = metric ? Number(metric.amount) || 0 : null;
  /* Scale progressive daily goals (Memberships 5/day) for Today / Last month /
     This year here at paint time — the only place that prints "of X". */
  let target = metric ? displayTargetOf(metric, kind, period, customRange) : 0;
  if (kind === "%" && !target) target = 1;
  const pct = target > 0 && value != null ? value / target : 0;
  const graph = row.graph || "number";
  /* Only a goal the admin actually set is a goal. A rate with no target still
     gets a 100% scale above so the ring/gauge can draw, but printing
     "target 100%" or "44% of target" off that scale would state a goal nobody
     set. Every graph, Number included, shows the "of X" sub-line and the
     progress bar whenever a real goal exists — Number was stripped of both in
     #2084 and every tile on the board silently lost its goal indicator. */
  const hasGoal = kind === "%" ? Number(metric?.target) > 0 : target > 0;
  const usesGoal = hasGoal;
  /* Resolve here (after value/target) so "By goal" can flip green/red. */
  const colour = resolveMetricColor(
    row.color,
    accent || primary,
    primary,
    { value, target },
  );

  let center = fmt(kind, value, metric);
  if (kind === "%") center = fmt("%", value, metric);
  else if (target > 0) center = `${Math.round(pct * 100)}%`;

  const isRate = isRateLikeKpi(metric);
  const rangeKey = customRange ? `${customRange.from || ""}~${customRange.to || ""}` : "";
  const series = useMemo(() => {
    /* Daily points across the SELECTED window, not always the current month:
       ▲/▼ on a Last week / quarter / custom board used to fold this month's
       days and report a trend the tile's own number never came from.
       Cards often store a title / remapped id while seriesByDay is keyed by
       the real KPI id — try every candidate. */
    if (!row.metricId || !seriesByDay) return [];
    const keys = [];
    const push = (v) => {
      const s = v != null ? String(v) : "";
      if (s && !keys.includes(s)) keys.push(s);
    };
    push(row.metricId);
    push(metric && metric._id);
    (metric && metric._seriesIds ? metric._seriesIds : []).forEach(push);
    (metric && metric._sourceIds ? metric._sourceIds : []).forEach(push);
    try {
      const { from, to } = resolveWindow(period === "year" ? "ytd" : period, customRange);
      /* Clip to company-today so future empty days don't force a fake ▼100%. */
      const cap = companyToday().endOf("day");
      const end = to && to.isAfter(cap) ? cap : to;
      if (!from || !end || !from.isValid() || !end.isValid() || end.isBefore(from, "day")) return [];
      const daysFor = (kpiId) => {
        const out = [];
        const cursor = from.clone().startOf("day");
        while (cursor.isSameOrBefore(end, "day")) {
          const cell = (seriesByDay[cursor.format("YYYY-MM-DD")] || {})[kpiId];
          let dayValue = 0;
          if (cell && typeof cell.amt === "number") dayValue = cell.amt;
          else if (typeof cell === "number") dayValue = cell;
          out.push({ timestamp: cursor.format("YYYY-MM-DD"), value: dayValue });
          cursor.add(1, "day");
        }
        return out;
      };
      let daily = [];
      for (let i = 0; i < keys.length; i += 1) {
        const cand = daysFor(keys[i]);
        if (cand.some((d) => Number(d?.value) > 0)) {
          daily = cand;
          break;
        }
      }
      if (!daily.length && keys.length) daily = daysFor(keys[0]);
      return daily;
    } catch (e) {
      return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seriesByDay, row.metricId, metric, period, rangeKey]);

  /* Half-vs-half of the window's days (same idea as Analytics StatTile).
     Both halves must span the SAME number of days or a summed metric on an
     odd-length window (7-day Last week → 3 vs 4) reads ~33% up on volume
     alone; the middle day is dropped instead. */
  /* A count-like metric (Memberships, jobs, …) summed over a half with only a
     handful of events turns an ordinary day-to-day swing into a triple-digit
     percentage: 3 → 10 is a real jump in absolute terms, but printing "▲233%"
     states a precision the underlying numbers don't have. Below this floor
     the ratio is noise, not a trend, so it's hidden the same way a rate with
     no readings in a half already is — never shown wrong. */
  const MIN_SUM_BASE = 5;
  /* Returns the % plus what it was measured on (both halves' date ranges and
     values) so the chip can say so on hover — a bare "▼9%" reads as "vs last
     month" or "vs goal", and it is neither. */
  const deltaInfo = useMemo(() => {
    if (!series.length || series.length < 2) return null;
    const mid = Math.floor(series.length / 2);
    const left = series.slice(0, mid);
    const right = series.slice(series.length - mid);
    /* A rate with no readings in a half is unknown, not 0%. Closed Rate, AVG
       Ticket, Bid Accuracy and composites are month snapshots written on the
       1st, so the second half is always empty — reading that as 0 painted a
       fake ▼100% on every one of them. */
    const fold = (pts) => {
      if (isRate) {
        const nz = pts.map((p) => Number(p.value) || 0).filter((v) => v > 0);
        return nz.length ? nz.reduce((a, b) => a + b, 0) / nz.length : null;
      }
      return pts.reduce((s, p) => s + (Number(p.value) || 0), 0);
    };
    const first = fold(left);
    const second = fold(right);
    if (first == null || second == null) return null;
    if (!(first > 0) || !Number.isFinite(second)) return null;
    if (!isRate && (first < MIN_SUM_BASE || second < MIN_SUM_BASE)) return null;
    const change = ((second - first) / first) * 100;
    if (!Number.isFinite(change)) return null;
    const span = (pts) => {
      const a = moment(pts[0].timestamp);
      const b = moment(pts[pts.length - 1].timestamp);
      return a.isSame(b, "day") ? a.format("MMM D") : `${a.format("MMM D")}–${b.format(a.isSame(b, "month") ? "D" : "MMM D")}`;
    };
    return { pct: change, first, second, firstSpan: span(left), secondSpan: span(right) };
  }, [series, isRate]);
  const deltaPct = deltaInfo ? deltaInfo.pct : null;
  const deltaTip = deltaInfo ? {
    eyebrow: `Trend · ${periodLabel === "this month" ? "this month so far" : periodLabel}`,
    rows: [
      { label: deltaInfo.secondSpan, value: fmt(kind, deltaInfo.second, metric) },
      { label: deltaInfo.firstSpan, value: fmt(kind, deltaInfo.first, metric) },
    ],
    verdict: {
      text: `${deltaInfo.pct >= 0 ? "▲" : "▼"} ${Math.abs(deltaInfo.pct).toFixed(0)}% ${deltaInfo.pct >= 0 ? "higher" : "lower"}`,
      tone: deltaInfo.pct >= 0 ? "good" : "bad",
    },
    note: `Second half vs first half, not vs last period or goal.${isRate ? " Rates: avg of days with a reading." : ""}`,
  } : null;

  // Spark / bar art still needs ≥2 non-zero week-ish points; reuse elapsed days.
  const liveSeries = series.filter((d) => Number(d.value) > 0).length >= 2;

  /* Hold the tile shape while scores load / the date filter refetches — never
     flash $0 or "Not available yet" for a metric that is about to resolve. */
  if (loading) {
    return (
      <div className="gmb-skel-metric" aria-busy="true" aria-label="Loading metric">
        <span className="gmb-skel label" />
        <span className="gmb-skel value" />
        <span className="gmb-skel sub" />
        <span className="gmb-skel bar" />
      </div>
    );
  }

  if (!metric) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 5, padding: "8px 0" }}>
        <span style={{ fontSize: 12.5, fontWeight: 500, color: "var(--tvd-body, #475467)" }}>Not available yet</span>
        <span style={{ fontSize: 11, color: "var(--tvd-muted, #98A2B3)", lineHeight: 1.5 }}>This metric isn’t live in this workspace.</span>
      </div>
    );
  }

  /* How the tile stands against the Yeti — the same pace the Beat the Yeti card
     draws (see yetiPace.js). Drawn only when the Yeti really has a pace on this
     metric for this window; otherwise nothing, never a made-up 0. */
  let yetiLine = null;
  /* A goal typed on the card (Revenue per Hour) is the card's own, not the
     Yeti's: when the two differ, the Yeti's pace is not shown beside it. */
  const ownGoalDiffers = !!(metric._derived && metric._fixedTarget && metric._yeti
    && fmt(kind, target, metric) !== fmt(kind, metric._yeti.amt, metric));
  if (!ownGoalDiffers && metric._yeti && Number(metric._yeti.amt) > 0) {
    const st = yetiStanding(value, metric._yeti.amt, metric, (n) => fmt(kind, n, metric));
    let tone = "#D93D42";
    if (st.level) tone = "var(--tvd-muted, #98A2B3)";
    else if (st.ahead) tone = "#299E47";
    let standing = `${st.diffText} behind`;
    if (st.level) standing = "level with the Yeti";
    else if (st.ahead) standing = `${st.diffText} ahead`;
    const many = metric._yeti.workspaces > 1;
    let verdictTone = "bad";
    if (st.level) verdictTone = "flat";
    else if (st.ahead) verdictTone = "good";
    const yetiTip = {
      eyebrow: `The Yeti · ${periodLabel}`,
      rows: [
        { label: metric._yeti.team ? `Yeti pace · ${metric._yeti.team} reps` : "Yeti pace", value: fmt(kind, metric._yeti.amt, metric) },
        { label: "You", value: fmt(kind, value, metric) },
      ],
      verdict: { text: `${st.level ? "" : `${st.ahead ? "▲" : "▼"} `}${standing}`, tone: verdictTone },
      note: metric._yeti.team
        ? "Team total vs each rep's Yeti pace × reps who scored."
        : `Same as the Beat the Yeti card${many ? " · avg of workspaces" : ""}.`,
    };
    /* Yeti pace and goal are the same number (a metric whose Yeti isn't
       prorated). Printing it twice, with a "behind" that repeats the gap to the
       goal, is noise — say it once, as a badge on the goal line. */
    const sameAsGoal = usesGoal && fmt(kind, target, metric) === fmt(kind, metric._yeti.amt, metric);
    let toneBg = "rgba(217,61,66,0.10)";
    if (st.level) toneBg = "var(--tvd-tile, #EEF1F4)";
    else if (st.ahead) toneBg = "rgba(41,158,71,0.10)";
    if (sameAsGoal) {
      /* The goal IS the Yeti's pace, so the goal line above already names it;
         all that is left to say is where the tile stands. One small chip, the
         snowflake marking whose pace it is. */
      yetiLine = (
        <div style={{ marginTop: 5, display: "flex" }}>
          <TileTip {...yetiTip} note={`Yeti pace = this goal. ${yetiTip.note}`}>
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 4, padding: "1px 7px", borderRadius: 999,
              fontSize: 9.5, fontWeight: 600, whiteSpace: "nowrap", color: tone, background: toneBg,
            }}
            >
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden>
                <path d="M12 2v20M4.5 6.5l15 11M19.5 6.5l-15 11" />
              </svg>
              {!st.level && <span aria-hidden>{st.ahead ? "▲" : "▼"}</span>}
              {standing}
            </span>
          </TileTip>
        </div>
      );
    } else yetiLine = (
      <div style={{ fontSize: 10.5, color: "var(--tvd-muted, #98A2B3)", marginTop: 3 }}>
        <TileTip {...yetiTip}>
          <span style={{ display: "inline-flex", alignItems: "baseline", gap: 5, flexWrap: "wrap" }}>
            <span>Yeti {fmt(kind, metric._yeti.amt, metric)}</span>
            <span style={{ color: tone, fontWeight: 600 }}>
              {!st.level && <span aria-hidden>{st.ahead ? "▲ " : "▼ "}</span>}
              {standing}
            </span>
          </span>
        </TileTip>
      </div>
    );
  }

  /* Every metric reads the same way, whatever its graph: name on top, the
     number, then how it's pacing. The graph sits under that — it decorates the
     number, it never replaces its labelling. */
  const head = (
    <>
      <div style={{ fontSize: 10.5, fontWeight: 500, color: "var(--tvd-muted, #98A2B3)", marginBottom: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {label}
      </div>
      <div style={{
        fontSize: big ? 34 : 25,
        fontWeight: 600,
        color: "var(--tvd-ink, #101828)",
        lineHeight: 1.02,
        letterSpacing: "-0.02em",
        display: "flex",
        alignItems: "baseline",
        gap: 8,
        flexWrap: "wrap",
      }}
      >
        <span>{fmt(kind, value, metric)}</span>
        {/* Beat the Yeti rate with the count by team on: the rows' total. Held
            back while a team's own read is still landing (its counts are 0 of 0
            until then). */}
        {metric._yetiTeams && metric._yetiCount && !metric._yetiTeams.some((t) => t.loading) && (
          <YetiTotal beating={metric._yetiCount.beating} players={metric._yetiCount.players} periodLabel={periodLabel} />
        )}
        {/* Average Daily Revenue / Technicians' Revenue per Hour: the
            first-half-vs-second-half-of-the-window split reads as a wild
            swing on a naturally spiky daily number, without meaning
            "down X% overall" the way it does for a steadier metric. */}
        {deltaPct != null && !metric?._derived && (
          <TileTip {...deltaTip}>
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: deltaPct >= 0 ? "#299E47" : "#D93D42",
                display: "inline-flex",
                alignItems: "center",
                gap: 3,
                letterSpacing: 0,
              }}
            >
              <span aria-hidden>{deltaPct >= 0 ? "▲" : "▼"}</span>
              {Math.abs(deltaPct).toFixed(0)}%
            </span>
          </TileTip>
        )}
      </div>
      {/* A metric with no target has nothing to be a percentage of. Saying
          "of $0" and drawing a progress bar against it is a made-up statement,
          so the sub-line falls back to the period and the graph below falls
          back to the trend (see `art`). */}
      <div style={{ fontSize: 10.5, color: "var(--tvd-muted, #98A2B3)", marginTop: 3 }}>
        {(() => {
          if (!usesGoal) return periodLabel;
          if (kind === "%") return `target ${fmt("%", target, metric)}`;
          /* "of $200" under a per-hour figure reads like a unit; say it's the goal. */
          return `${metric._derived ? "Goal" : "of"} ${fmt(kind, target, metric)}`;
        })()}
        {metric.pace ? ` · pace ${fmt(kind, Number(metric.pace), metric)}` : ""}
      </div>
      {yetiLine}
      {/* Beat the Yeti rate only: the overall bar + goal notch, then a row per
          tracked workspace — each one an opt-in from the card's settings. */}
      {metric._yetiBar && <YetiOverall rate={value} goal={metric._yetiGoal} />}
      {metric._yetiTeams && <YetiTeams teams={metric._yetiTeams} periodLabel={periodLabel} />}
    </>
  );

  // Circular graphs replace the number with their own centred readout.
  if (graph === "ring" || graph === "donut" || graph === "gauge") {
    let Shape = Ring;
    if (graph === "donut") Shape = Donut;
    else if (graph === "gauge") Shape = Gauge;
    return (
      <div>
        <div style={{ fontSize: 10.5, fontWeight: 500, color: "var(--tvd-muted, #98A2B3)", marginBottom: 6, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</div>
        <Shape pct={pct} color={colour} center={center} sub={hasGoal ? pctLabel(pct) : ""} />
        {yetiLine}
        {metric._yetiBar && <YetiOverall rate={value} goal={metric._yetiGoal} />}
        {metric._yetiTeams && <YetiTeams teams={metric._yetiTeams} periodLabel={periodLabel} />}
      </div>
    );
  }

  let art = null;
  if (SERIES_GRAPHS.includes(graph) && liveSeries) {
    if (metric?._derived) {
      /* Average Daily Revenue / Technicians' Revenue per Hour only — the same
         Recharts chart chrome Goal Progress uses (real axes, tooltip,
         gradient fill), not the hand-rolled MetricSparkline/MiniBars every
         other Build Your Own card still renders through. */
      art = (
        <DerivedTrendChart
          series={series}
          kind={kind}
          color={colour}
          style={graph}
          gradientId={`dtc-${row.metricId}-${chartInstanceId}`}
          height={graph === "spark" ? 44 : 220}
        />
      );
    } else {
      let op = 0.55;
      if (graph === "area") op = 1.3;
      else if (graph === "line") op = 0;
      art = graph === "bar"
        ? <MiniBars series={series} color={colour} />
        : <MetricSparkline data={series} color={colour} height={graph === "spark" ? 44 : 62} gradient={graph !== "line"} opacity={op} />;
    }
  } else if (usesGoal && metric._derived && kind === "$") {
    /* A computed dollar metric (Technicians' Revenue per Hour): the bar is drawn
       on a track that runs a quarter past the goal, so the goal is a real mark
       partway along it — labelled with its value — and beating it shows as the
       fill carrying on past the mark. The fill is red until the goal is
       reached and green after, like the Beat the Yeti bars. On a bar that simply ends at the goal
       there is nothing to mark. */
    art = (
      <div className="cw-goalbar">
        <span className="cw-goalbar-lbl" style={{ left: `${GOAL_MARK_AT}%` }}>{fmt(kind, target, metric)}</span>
        <div className="cw-goalbar-rail">
          <div className="cw-goalbar-fill" style={{ width: `${Math.max(2, Math.min(100, pct * GOAL_MARK_AT))}%`, background: pct >= 1 ? "#299e47" : "#d93d42" }} />
        </div>
        <i className="cw-goalbar-tick" style={{ left: `${GOAL_MARK_AT}%` }} title={`Goal ${fmt(kind, target, metric)}`} />
      </div>
    );
  } else if (usesGoal) {
    // No series (or none worth drawing) → the progress toward target carries it.
    art = (
      <div style={{ height: 6, borderRadius: 999, background: "var(--tvd-tile, #EEF1F4)", overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${Math.max(2, Math.min(100, pct * 100))}%`, borderRadius: 999, background: colour, transition: "width 700ms ease" }} />
      </div>
    );
  }

  return (
    <div style={{ minWidth: 0 }}>
      {head}
      {art && <div style={{ marginTop: 9 }}>{art}</div>}
    </div>
  );
};

/* The payoff band — only rendered when a bonus rule exists. */
const Payoff = ({ kpis, bonus }) => {
  const src = (kpis || []).find((k) => k && metricIdEq(k._id, bonus.ofMetric)) || null;
  if (!src) return null;
  const kind = kindOf(src);
  const base = Number(src.amount) || 0;
  const earned = (base * (Number(bonus.pct) || 0)) / 100;
  return (
    /* Neutral, not green — deliberately. Green already means "up / good" all
       over the board (deltas, trend arrows), so spending it on "this is money"
       blurs the signal players read fastest. It is also a hardcoded semantic
       colour, which ignores the tenant's own branding. This is the same band as
       "Total this month" on Anticipated earnings, so the two money cards sitting
       side by side finally read as a pair. */
    <div style={{
      marginTop: 15, borderRadius: 11, padding: "12px 14px", display: "flex", alignItems: "center",
      justifyContent: "space-between", gap: 10,
      background: "var(--tvd-tile, #EDF1F6)", border: "1px solid var(--tvd-hair, #E2E8F1)",
    }}>
      <div>
        <div style={{ fontSize: 11, fontWeight: 500, color: "var(--tvd-body, #475467)" }}>{bonus.label || "Earned"}</div>
        <div style={{ fontSize: 10, color: "var(--tvd-muted, #98A2B3)", marginTop: 2 }}>{bonus.pct}% × {fmt(kind, base)}</div>
      </div>
      <div style={{ fontSize: 21, fontWeight: 600, color: "var(--tvd-ink, #0F172A)", letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums" }}>
        {fmt("$", earned)}
      </div>
    </div>
  );
};

/* The payout curve band — a settable ladder (Advanced) drawn as a curve and
   driven by the component's own metric. The marker sits where the live value
   lands, the reached tier is emphasised, and the projected payout reads off it.
   Only the on-screen projection lives here; the amount the server credits is a
   separate step. */
const PayoutCurveBand = ({ metric, curve, gateKpis, gateWorkspaceIds }) => {
  const [popOpen, setPopOpen] = useState(false);
  const [hoverTick, setHoverTick] = useState(null);
  const lineRef = useRef(null);
  const tiers = (curve && curve.tiers) || [];

  /* Gates read their own metric off the live pool. A gate whose metric isn't in
     this workspace resolves to nothing and is reported as "not set up yet" — it
     never silently withholds money on a missing reading.
     A same-named metric can live on MORE THAN ONE of the card's own assigned
     workspaces (Closed Rate on Field Sales AND In-House Sales) — that is more
     than one KPI definition sharing a title, not one. Pool every definition
     tagged to one of the card's own workspaces (`_wsId`, see ScopedCustomWidget)
     and combine them per the gate's own mode, instead of reading whichever one
     happened to come first in the pool. Falls back to that single-match
     behaviour when the gate targets a metric outside the card's own scope (a
     health metric on another team) or workspace tags aren't available. */
  const readMetric = useCallback((g) => {
    const id = String(g && g.metric ? g.metric : "");
    if (!id) return { found: false };
    const pool = gateKpis || [];
    const want = id.trim().toLowerCase();
    const isMatch = (k) => k && (metricIdEq(k._id, id) || `${k?.title || k?.name || ""}`.trim().toLowerCase() === want);

    const scopeIds = (gateWorkspaceIds || []).map(String).filter(Boolean);
    const seen = new Set();
    const dedupe = (arr) => arr.filter((k) => {
      const key = String(k._id || `${k.title || k.name || ""}`);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    let hits = scopeIds.length
      ? dedupe(pool.filter((k) => isMatch(k) && k._wsId && scopeIds.includes(String(k._wsId))))
      : [];
    if (!hits.length) {
      const first = pool.find((k) => k && metricIdEq(k._id, id)) || pool.find(isMatch);
      if (!first) return { found: false };
      hits = [first];
    }

    /* Rates are stored 0–1; the admin types 50 meaning 50%. */
    const vals = hits.map((k) => {
      let v = Number(k.amount) || 0;
      if (g.unit !== "$" && Math.abs(v) <= 1) v *= 100;
      return v;
    });
    const value = g.mode === "sum"
      ? vals.reduce((a, b) => a + b, 0)
      : vals.reduce((a, b) => a + b, 0) / vals.length;
    return { found: true, value };
  }, [gateKpis, gateWorkspaceIds]);

  if (!curve || !curve.on || !tiers.length) return null;
  if (!metric) {
    return (
      <div style={{ padding: "12px 0", color: "var(--tvd-muted, #98A2B3)", fontSize: 12.5 }}>
        Payout curve waits on the metric on the left.
      </div>
    );
  }
  const value = Number(metric.amount) || 0;
  /* Defend against non-numeric targets (NaN → 0 used to max every % tier). */
  const target = Number(metric.target) || 0;
  const pctTiers = tiers.some((t) => t && t.unit !== "$");
  if (pctTiers && target <= 0) {
    return (
      <div style={{ padding: "12px 0", color: "var(--tvd-muted, #98A2B3)", fontSize: 12.5 }}>
        Add a target on the paired KPIs — % tiers need it to place the marker.
      </div>
    );
  }
  /* Gates decide what the curve actually pays: the marker reads the GATED
     figure, so a rep sitting past the 110% tick while a gate is short sees the
     100% amount and the line underneath says why. */
  const res = payoutWithGates(value, target, tiers, readMetric);
  const pay = Math.round(res.pay);
  const geo = curveGeometry(value, target, tiers);
  const ordered = sortedTiers(tiers, target);
  const reached = reachedTierIndex(value, target, tiers);
  const x = geo.xForValue;
  const attain = target > 0 ? Math.round((value / target) * 100) : null;
  /* Below the first tier, projected payout is $0 — show attainment % so the
     marker doesn't look like the whole card is at zero. */
  let markerText = "$0";
  if (pay > 0) markerText = fmt("$", pay);
  else if (attain != null) markerText = `${attain}%`;
  /* Green once the TOP FUNDED tier is cleared — unfunded seed rows above it
     (pay $0) are not rungs, so waiting for the last index never turned green. */
  let topFunded = -1;
  ordered.forEach((t, i) => { if ((Number(t.pay) || 0) > 0) topFunded = i; });
  let hue = "#F79009";
  if (reached < 0) hue = "#F04438";
  else if (topFunded >= 0 && reached >= topFunded) hue = "#12B76A";
  /* Tiers bunched together (e.g. 90/95/100%) land within a few % of each
     other on the x axis no matter how wide the curve is drawn — 90 and 100
     are only 10% of the value range apart, so more pixels stretches the GAP
     but never enough for two stacked lines of text to clear it. Widening
     alone can't fix that; a close tick instead drops to the next of 3
     vertical rows (resetting once there's breathing room again), so its
     label still prints in full, just lower, instead of overlapping its
     neighbour's or hiding behind a hover. */
  const MIN_TICK_GAP = 7;
  const ROW_GAP = 30;
  let rowCursor = 0;
  const tickRows = geo.ticks.map((t, i) => {
    const prev = geo.ticks[i - 1];
    const closePrev = !!(prev && t.x - prev.x < MIN_TICK_GAP);
    rowCursor = closePrev ? (rowCursor + 1) % 3 : 0;
    return rowCursor;
  });
  const maxTickRow = tickRows.length ? Math.max(...tickRows) : 0;

  /* What the line under the curve says, and the colour it says it in.
     One ladder, four steps: nothing cleared, moving, one to go, unlocked.
     Deliberately no red — mid-period nobody has failed, they have just not
     arrived, and telling somebody on 3 of 4 that they are in the red is the
     opposite of useful. Red belongs to a closed period, which is a later job. */
  let gatedTier = null;
  if (res.isHeld) gatedTier = res.heldTier;
  else if (reached >= 0 && ordered[reached] && ordered[reached].gate) gatedTier = ordered[reached];

  let gline = null;
  if (gatedTier) {
    const all = res.isHeld ? (res.gateStates || []) : tierGateStates(gatedTier, readMetric).states;
    const live = (all || []).filter((s) => s.state !== "off");
    if (live.length) {
      const total = live.length;
      /* Cleared means "not holding the money up", so a gate that cannot judge
         anything yet counts with the ones that pass — same as the payout. */
      const cleared = live.filter((s) => !s.blocks).length;
      const label = tierLabel(gatedTier);
      const metricName = metric.title || metric.name || "This metric";
      let step = "s1";
      if (cleared >= total) step = "s3";
      else if (cleared === total - 1) step = "s2";
      else if (cleared === 0) step = "s0";

      let text;
      if (cleared >= total) text = `All ${total === 1 ? "of it" : `${total}`} met. The ${label} tier is paying in full.`;
      else if (cleared === total - 1) text = `One gate to go and the ${label} tier unlocks.`;
      else if (cleared === 0) text = `No gates cleared yet for the ${label} tier.`;
      else text = `${cleared} of ${total} gates cleared. The ${label} tier unlocks when they all are.`;

      let say;
      if (cleared >= total) {
        say = `${metricName} came in at ${attain}% of target and every gate the ${label} tier asks for is met, so the bonus is paying in full.`;
      } else {
        const head = `${metricName} came in at ${attain}% of target, which reaches the ${label} tier, ${fmt("$", res.earned)}. `;
        /* Gates hold back the step UP, not the bonus already earned on revenue,
           so the sentence names what is still paying rather than implying zero.
           Only the lowest tier has nothing under it to fall back to. */
        const still = res.pay > 0
          ? `it is paying ${fmt("$", res.pay)} and holding back ${fmt("$", res.held)}`
          : "it is not paying";
        if (cleared === 0) say = `${head}None of its ${total} gates are cleared yet, so ${still}.`;
        else if (cleared === total - 1) say = `${head}${cleared} of its ${total} gates are cleared, so ${still}. One more releases it.`;
        else say = `${head}${cleared} of its ${total} gates are cleared, so ${still}. All ${total} have to clear to release the rest.`;
      }

      /* The footer names the single closest thing they can act on. */
      let foot = "";
      if (cleared >= total) {
        foot = reached < ordered.length - 1
          ? `Next tier is ${tierLabel(ordered[reached + 1])} at ${fmt("$", ordered[reached + 1].pay)}.`
          : "";
      } else {
        const near = live.filter((s) => s.blocks).sort((a, b) => (b.pct || 0) - (a.pct || 0))[0];
        if (near) {
          const g = near.gate || {};
          const nm = g.name || g.metricName || "A gate";
          if (g.source === "manual") {
            /* What confirming is worth is the amount being HELD, not the whole
               tier — the tier below it is already paying. */
            foot = `${nm} is signed off by a person. Confirming it releases ${fmt("$", res.held)}.`;
          } else {
            const unit = (v) => (g.unit === "$" ? fmt("$", v) : `${Math.round(Number(v) || 0)}%`);
            foot = `${nm} is closest — ${unit(near.value)} against ${unit(near.min)}.`;
          }
        }
      }

      gline = { step, text, say, foot, tierLabel: label, states: live, cleared, total };
    }
  }

  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 500, color: "var(--tvd-muted, #98A2B3)", marginBottom: 6 }}>
        Payout curve · {(metric.title || metric.name || "").toLowerCase()}
        {attain != null ? ` · ${attain}% of target` : ""}
      </div>
      <div className="cw-curve-scroll">
        {/* Fills whatever width the card actually gives it — the row stagger
            above (not extra width) is what keeps close tiers from
            overlapping, so forcing the box wider than its real space used to
            do was just pushing 90/95/100% off the edge into a scrollbar. */}
        <div style={{ position: "relative", height: 116 + maxTickRow * ROW_GAP, width: "100%", minWidth: 320 }}>
          <div style={{ position: "absolute", top: 0, left: `${Math.max(9, Math.min(91, x))}%`, transform: "translateX(-50%)", whiteSpace: "nowrap" }}>
            <div style={{ background: "#101828", color: "#fff", fontSize: 11, fontWeight: 500, padding: "3px 8px", borderRadius: 6, fontVariantNumeric: "tabular-nums" }}>{markerText}</div>
            <div style={{ width: 0, height: 0, margin: "0 auto", borderLeft: "5px solid transparent", borderRight: "5px solid transparent", borderTop: "5px solid #101828" }} />
          </div>
          <div style={{ position: "absolute", top: 44, left: 0, right: 0, height: 10, borderRadius: 999, background: "linear-gradient(90deg,#F04438 0%,#F79009 40%,#12B76A 70%,#039855 100%)" }} />
          <div style={{ position: "absolute", top: 44, right: 0, width: `${100 - x}%`, height: 10, borderRadius: "0 999px 999px 0", background: "var(--tvd-veil, rgba(255,255,255,0.62))" }} />
          <div style={{ position: "absolute", top: 39, left: `${x}%`, width: 20, height: 20, marginLeft: -10, borderRadius: "50%", background: hue, border: "3px solid #fff", boxShadow: `0 2px 7px ${hue}66` }} />
          {geo.ticks.map((t, i) => {
            /* The goal-side value this tier's % resolves to (target x % for a
               percent tier, the raw figure for a $ tier) — Kelvin's call: the
               curve reads target attainment, not the payout amount, so a 10%
               tier under a $1,000,000 goal reads "10% / $100,000", not its pay. */
            const under = Number(t.thr) || 0;
            /* A padlock marks a tier that carries gates, and turns red on the
               one actually holding the money back. */
            const gated = !!(ordered[t.i] && ordered[t.i].gate && (ordered[t.i].gates || []).some((g) => g && g.on !== false));
            const holding = gated && res.heldTierIdx === t.i;
            const row = tickRows[i];
            const showTip = hoverTick === t.i;
            let tipLock = "";
            if (gated) tipLock = holding ? " 🔒 not met" : " 🔒";
            /* The connecting line runs from the curve down to WHICHEVER row
               this tick's label landed on — a crowded tick's line is just
               taller, so it still visibly points at its own spot on the bar
               even after dropping a row or two to clear its neighbour. */
            const lineHeight = 7 + row * ROW_GAP;
            return (
              <div
                key={`${t.label}-${t.i}`}
                style={{ position: "absolute", top: 60, left: `${t.x}%`, transform: "translateX(-50%)", textAlign: "center" }}
                onMouseEnter={() => setHoverTick(t.i)}
                onMouseLeave={() => setHoverTick((v) => (v === t.i ? null : v))}
              >
                {showTip && (
                  <div style={{ position: "absolute", top: -6, left: "50%", transform: "translate(-50%, -100%)", background: "#101828", color: "#fff", fontSize: 11, fontWeight: 500, padding: "3px 8px", borderRadius: 6, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", zIndex: 2 }}>
                    {/* The static line under the tick reads the goal side (this
                        tier's dollar value of target); hover answers the other
                        question — what it actually pays. */}
                    {t.label} pays {fmt("$", Number(t.pay) || 0)}{tipLock}
                  </div>
                )}
                <div style={{ width: 1, height: lineHeight, background: "var(--tvd-hair, #D0D5DD)", margin: "0 auto 5px", cursor: "help" }} />
                <div style={{ fontSize: 11, fontWeight: t.i === reached ? 600 : 500, color: t.i === reached ? hue : "var(--tvd-body, #475467)", whiteSpace: "nowrap" }}>
                  {t.label}
                  {gated && (
                    <span
                      aria-label={holding ? "gate not met" : "gated tier"}
                      style={{ fontSize: 9, marginLeft: 3, color: holding ? "#D92D20" : "var(--tvd-muted, #B6BFCA)" }}
                    >
                      🔒
                    </span>
                  )}
                </div>
                {/* The goal side is in the METRIC's unit, not money — a curve on
                    closed rate printed "$800" for what is 800%. A "$" tier is a
                    raw figure on that same metric, so it follows the metric too. */}
                <div style={{ fontSize: 10.5, color: t.i === reached ? "var(--tvd-ink, #344054)" : "var(--tvd-muted, #98A2B3)", fontVariantNumeric: "tabular-nums", marginTop: 1 }}>{fmt(kindOf(metric), under, metric)}</div>
              </div>
            );
          })}
        </div>
      </div>
      {gline && (
        <div ref={lineRef}>
          <GateLine step={gline.step} text={gline.text} open={popOpen} onToggle={() => setPopOpen((v) => !v)} />
        </div>
      )}
      {gline && popOpen && (
        <GatePopover
          anchorEl={lineRef.current}
          onClose={() => setPopOpen(false)}
          tierLabel={gline.tierLabel}
          paying={res.pay}
          unlocks={res.earned}
          cleared={gline.cleared}
          total={gline.total}
          step={gline.step}
          say={gline.say}
          states={gline.states}
          foot={gline.foot}
        />
      )}
    </div>
  );
};

/* ── the card ────────────────────────────────────────────────────────────── */

const ALL_PLAYERS = "__all__";

const CustomWidget = ({
  kpis = [], seriesByDay = null, metrics = [], bonus = null, payoutCurve = null, pieceLayout = null, look = null, title, gateKpis = null,
  gateWorkspaceIds = null,
  primary = "#1B598E", show = "", showOn = null, period = "month", customRange = null, ownerId = null, loading = false, refreshing = false,
  canSwitch = false,
}) => {
  /* Whose numbers the card is about. Admins can point it at any player; a
     player only ever sees themselves, so this is gated on the viewer's role
     upstream and never rendered for a rep. Switching rewrites the metric values
     from that player's own scores, which is why everything below — the bonus,
     the ranking highlight, the activity list — follows without extra wiring. */
  const teamAll = UseTopPlayers(["today", "yesterday", "week", "lastWeek"].includes(period) ? period : "month");
  const workspace = UseSelectedWorkspace();
  const usersDict = UseUsersDictionary();
  /* The full workspace roster, not just whoever has scored. UseTopPlayers is
     built from the score records, so a player with no entries this month never
     appeared in the picker at all — but the card is a lens you point at a
     person, and a person with a zero is still a person. Scored players keep
     their standing order; the rest of the roster follows alphabetically. */
  const team = useMemo(() => {
    const scored = (Array.isArray(teamAll) ? teamAll : []).filter(
      (p) => p && !p.isYeti && p.userId && p.isArchived !== true && p.isActive !== false,
    );
    const seen = new Set(scored.map((p) => String(p.userId)));
    const dict = usersDict || {};
    const wsId = workspace?._id ? String(workspace._id) : null;
    const unscored = Object.keys(dict)
      .filter((id) => {
        if (seen.has(id)) return false;
        const u = dict[id];
        if (!u || u.isArchived === true || u.isActive === false || u.isAdmin) return false;
        const gid = u.groupId?._id || u.groupId;
        return !!wsId && !!gid && String(gid) === wsId;
      })
      .map((id) => ({ ...dict[id], userId: id, scores: null }))
      .sort((a, b) =>
        `${a.firstName || ""} ${a.lastName || ""}`.localeCompare(`${b.firstName || ""} ${b.lastName || ""}`),
      );
    return [...scored, ...unscored];
  }, [teamAll, usersDict, workspace?._id]);
  const [pickedId, setPickedId] = useState(null);
  /* An admin is usually not a player, so defaulting the picker to "me" left it
     reading "Select" with nobody chosen while the card quietly showed the
     admin's own empty numbers. Fall back to the player at the top instead: the
     card is always about somebody, and it says who. */
  const selfIsPlayer = team.some((p) => p.userId === ownerId);
  const defaultId = selfIsPlayer ? ownerId : team[0]?.userId || null;
  const viewId = canSwitch ? (pickedId || defaultId) : ownerId;
  const isAllView = canSwitch && viewId === ALL_PLAYERS;
  /* Only override the numbers when the card is pointed at somebody other than
     the viewer — for the viewer, ctx.kpis is the authoritative figure. */
  const picked = !isAllView && viewId && viewId !== ownerId ? team.find((p) => p.userId === viewId) : null;
  /* One override at the top and the whole card follows: every metric's amount
     is replaced with the chosen player's own figure. Metrics that player has no
     entry for keep a null amount, which the blocks already render as "not
     available" rather than as a zero they did not earn.
     "All players" rolls the roster up instead: counts and dollars are the
     team's sum; a rate is the average of those who actually scored it, because
     adding percentages together states a number nobody achieved. */
  const liveKpis = useMemo(() => {
    if (isAllView) {
      return (kpis || []).map((k) => {
        /* A derived tile (Revenue per Hour, Beat the Yeti rate) is already the
           team's reading — there is no per-player score to roll up, and
           rolling up nothing zeroed it along with any bonus paid on it. */
        if (!k || k._derived) return k;
        const isRate = isRateLikeKpi(k);
        const owns = team
          .map((p) => (p.scores ? p.scores[k._id] : null))
          /* Rates: only players who actually scored (>0). Unscored zeros were
             crushing Inside Sales Closed Rate to ~1% on "All players". */
          .filter((own) => {
            if (!own || own.amt == null) return false;
            if (isRate) return (Number(own.amt) || 0) > 0;
            return true;
          });
        const sum = owns.reduce((s, own) => s + (Number(own.amt) || 0), 0);
        let amount = sum;
        if (isRate) amount = owns.length ? sum / owns.length : 0;
        return { ...k, amount };
      });
    }
    if (!picked) return kpis || [];
    return (kpis || []).map((k) => {
      /* Same for one player: a team reading does not change with whose card
         it is. */
      if (k && k._derived) return k;
      const own = k && picked.scores ? picked.scores[k._id] : null;
      return own ? { ...k, amount: Number(own.amt) || 0 } : { ...k, amount: 0 };
    });
  }, [kpis, picked, isAllView, team]);

  const rows = (metrics || []).filter((r) => r && r.metricId);
  /* Creating with one metric picks Focus; adding more later left look stuck on
     Focus so only the first tile rendered. Promote to the grid automatically. */
  const effectiveLook = (look === "focus" && rows.length >= 2) ? "duo" : look;
  /* One reading of the bonus for the whole card: a commission ({ pct }) or a
     step ladder ({ type: "steps" }), sanitized here so a half-typed draft in
     the editor never paints a broken band. */
  const liveBonus = useMemo(() => sanitizeBonus(bonus), [bonus]);
  const stepBonus = isStepBonus(liveBonus) ? liveBonus : null;
  const commission = liveBonus && !stepBonus ? liveBonus : null;
  const firstMetric = rows.length ? (liveKpis || []).find((k) => k && metricIdEq(k._id, rows[0].metricId)) : null;
  const heading = (title && title.trim()) || (firstMetric?.title || firstMetric?.name || "").trim() || "Custom widget";
  const bonusSrc = liveBonus ? (liveKpis || []).find((k) => k && metricIdEq(k._id, liveBonus.ofMetric)) : null;

  // Two metrics get two accents so the card reads as two things, not one wall.
  /* One accent per tile, cycling — with four metrics a two-colour list paired
     diagonally opposite tiles and read as "these two belong together". */
  const ACCENTS = [primary, "#EC2786", "#12A150", "#B54708"];
  /* The cycle is only what a metric starts with. Once someone picks a colour in
     Visualisation it lives on the row, and it drives the target bar, the
     sparkline and the ring together — one swatch restyles the whole metric. */
  const periodLabel = periodWindowLabel(period, customRange);
  const block = (row, big, i = 0) => (
    <MetricBlock
      key={row.metricId}
      kpis={liveKpis}
      seriesByDay={seriesByDay}
      row={row}
      primary={primary}
      accent={ACCENTS[i % ACCENTS.length]}
      big={big}
      periodLabel={periodLabel}
      period={period}
      customRange={customRange}
      loading={loading}
    />
  );

  /* What sits under the numbers — one block, never two. `showOn` names the
     metric it is about; with more than one metric on the card, letting the code
     pick the first would be a silent decision nobody could account for. The
     heading takes the metric's own words ("Recent jobs", "Recent memberships")
     so the block reads as purpose-built wherever it is used. */
  const showRow = rows.find((r) => r.metricId === showOn) || rows[0] || null;
  const showMetric = showRow ? (liveKpis || []).find((k) => k && metricIdEq(k._id, showRow.metricId)) : null;
  const showKind = kindOf(showMetric);
  const showName = (showMetric?.title || showMetric?.name || "").trim();
  let underneath = null;
  if (show && showRow) {
    const blockTitle = show === "ranking" ? "Team ranking" : `Recent ${showName.toLowerCase() || "entries"}`;
    underneath = (
      <div style={{ marginTop: 18, paddingTop: 16, borderTop: "1px solid var(--tvd-hair, #F0F3F7)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 11 }}>
          <b style={{ fontSize: 11.5, fontWeight: 600, color: "var(--tvd-ink, #344054)" }}>{blockTitle}</b>
          <span style={{ fontSize: 10.5, color: "var(--tvd-muted, #98A2B3)" }}>
            {show === "ranking" ? `${showName} · ${periodLabel}` : periodLabel}
          </span>
        </div>
        {/* All-players view drops Activity's owner filter — the list becomes
            every entry behind the team total, which is what "all" means here. */}
        {show === "ranking" ? (
          <Standing metricId={showRow.metricId} metricTitle={showName} kind={showKind} period={period} primary={primary} />
        ) : (
          <Activity
            metricId={(showMetric?._sourceIds && showMetric._sourceIds.length)
              ? showMetric._sourceIds
              : showRow.metricId}
            metricTitle={showName}
            ownerId={isAllView ? null : viewId}
            kind={showKind}
            period={period}
            customRange={customRange}
          />
        )}
      </div>
    );
  }

  /* The money under the numbers: a commission's payoff band, or the step
     ladder for a rate / per-hour / team metric. */
  let bonusBand = null;
  if (commission) bonusBand = <Payoff kpis={liveKpis} bonus={commission} />;
  else if (stepBonus) {
    bonusBand = (
      <BonusLadder
        bonus={stepBonus}
        metric={bonusSrc}
        kind={kindOf(bonusSrc)}
        primary={primary}
        periodLabel={periodLabel}
        loading={loading}
        showName={!rows.some((r) => metricIdEq(r.metricId, stepBonus.ofMetric))}
      />
    );
  }

  let body;
  if (!rows.length) {
    body = (
      <div style={{ textAlign: "center", color: "var(--tvd-muted, #98A2B3)", fontSize: 12.5, maxWidth: 240, lineHeight: 1.6 }}>
        Pick a metric for this widget and its value will show here.
      </div>
    );
  } else if (effectiveLook === "meter" && bonusSrc && commission) {
    const kind = kindOf(bonusSrc);
    const base = Number(bonusSrc.amount) || 0;
    const pace = Number(bonusSrc.pace) || Number(bonusSrc.target) || base || 1;
    const earned = (base * (Number(commission.pct) || 0)) / 100;
    body = (
      <div style={{ width: "100%" }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
          <span style={{ fontSize: 27, fontWeight: 700, color: "#12A150", letterSpacing: "-0.02em" }}>{fmt("$", earned)}</span>
          <span style={{ fontSize: 11, color: "var(--tvd-muted, #98A2B3)" }}>{commission.label || "earned"}</span>
        </div>
        <div style={{ fontSize: 11, color: "var(--tvd-body, #475467)", margin: "5px 0 12px" }}>
          {fmt(kind, base, bonusSrc)} · {commission.pct}% commission
        </div>
        <div style={{ height: 13, borderRadius: 999, background: "var(--tvd-tile, #EEF1F4)", overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${Math.max(2, Math.min(100, (base / pace) * 100))}%`, borderRadius: 999, background: primary, transition: "width 700ms ease" }} />
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 13 }}>
          {rows.map((r) => {
            const m = (liveKpis || []).find((k) => k && metricIdEq(k._id, r.metricId));
            if (!m) return null;
            return (
              <div key={r.metricId} style={{ flex: 1, border: "1px solid var(--tvd-hair, #EEF1F4)", borderRadius: 10, padding: "8px 10px" }}>
                <div style={{ fontSize: 9.5, color: "var(--tvd-muted, #98A2B3)" }}>{(m.title || m.name || "").trim()}</div>
                <div style={{ fontSize: 15, fontWeight: 600, marginTop: 1 }}>{fmt(kindOf(m), Number(m.amount) || 0, m)}</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  } else if (rows.length >= 2 && effectiveLook !== "focus") {
    /* Two across at any count: three metrics wrap to 2 + 1, four to 2 + 2. A
       single row of four would leave each tile ~120px at half-card width, too
       narrow for a number and a graph. */
    body = (
      <div style={{ width: "100%" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 14, rowGap: 16, alignItems: "start" }}>
          {rows.map((r, i) => <div key={r.metricId} style={{ minWidth: 0 }}>{block(r, false, i)}</div>)}
        </div>
        {bonusBand}
        {underneath}
      </div>
    );
  } else {
    body = (
      <div style={{ width: "100%" }}>
        {block(rows[0], true)}
        {rows.length > 1 && (
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            {rows.slice(1).map((r) => {
              const m = (liveKpis || []).find((k) => k && metricIdEq(k._id, r.metricId));
              if (!m) return null;
              return (
                <div key={r.metricId} style={{ flex: 1, border: "1px solid var(--tvd-hair, #EEF1F4)", borderRadius: 10, padding: "8px 10px" }}>
                  <div style={{ fontSize: 9.5, color: "var(--tvd-muted, #98A2B3)" }}>{(m.title || m.name || "").trim()}</div>
                  <div style={{ fontSize: 15, fontWeight: 600, marginTop: 1 }}>{fmt(kindOf(m), Number(m.amount) || 0, m)}</div>
                </div>
              );
            })}
          </div>
        )}
        {bonusBand}
        {underneath}
      </div>
    );
  }

  /* The commission pill and the player picker share the header's right slot.
     Built here rather than inline so the two conditions read as one decision
     instead of a ternary inside a ternary. */
  let pillText = "";
  if (commission) pillText = `${commission.pct}% commission`;
  else if (stepBonus) {
    /* Beat the Yeti knows exactly who it pays (the scored players); any other
       per-player metric pays the card's roster. */
    const payees = bonusSrc?._yetiCount ? bonusSrc._yetiCount.players : team.length;
    pillText = bonusPillText(stepBonus, payees);
  }
  const bonusPill = pillText && !liveBonus.hidePill ? (
    <span style={{ fontSize: 10, fontWeight: 600, color: "#B54708", background: "rgba(245,179,1,0.16)", borderRadius: 999, padding: "3px 9px", whiteSpace: "nowrap" }}>
      {pillText}
    </span>
  ) : null;
  let headerRight = bonusPill;
  if (canSwitch && team.length > 1) {
    /* Measured against Anticipated earnings and matched to it: its picker is
       150px in a slot that does NOT grow, ending on the header's own padding.
       Mine was 200px inside a growing wrapper, so it sat at a different
       distance from the card edge than every other component's control. */
    headerRight = (
      <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {bonusPill}
        {/* 150px to match Anticipated earnings, but allowed to shrink: a custom
            card can be a quarter wide (327px), where a fixed 150px picker plus
            the title and the 86px button reserve does not fit and the control
            gets pushed past the header's padding. Shrinking keeps it on the
            same line as every other component's control at any card width. */}
        <span className="gmbw-cw-who" style={{ flex: "0 1 150px", minWidth: 104 }}>
          {/* 150 / 28 / 12 / 7 measured straight off the picker on Anticipated
              earnings. LightSelect defaults to 210 wide and 40 tall with 13px
              text and a 10px radius, which is why this read as a bigger control
              than every other card's — the width was never the whole mismatch. */}
          <LightSelect
            value={viewId || ""}
            /* LightSelect hands back the whole option object (unlike
               LightPicker, which Anticipated Earnings uses) — storing it raw
               matched nothing, so the trigger fell back to "Select" and the
               card never switched. Keep only the id. */
            onChange={(o) => setPickedId(o?.value ?? null)}
            width={150}
            height={28}
            fontSize={12}
            radius={7}
            options={[
              ...team.map((p) => ({
                value: p.userId,
                label: [p.firstName, p.lastName].filter(Boolean).join(" ") || "Player",
              })),
              { value: ALL_PLAYERS, label: "All players" },
            ]}
          />
        </span>
      </span>
    );
  }

  /* One shared metric object for the number block AND the payout curve — looking
     them up separately let the curve fall back to a kpi with target 0 and max
     every % tier while the left side showed "of $38,000". */
  const driveMetric = useMemo(() => {
    const list = liveKpis || [];
    if (!list.length) return null;
    const id = showOn || (rows[0] && rows[0].metricId) || null;
    if (id) {
      const hit = list.find((k) => k && metricIdEq(k._id, id));
      if (hit) return hit;
    }
    return list.length === 1 ? list[0] : null;
  }, [liveKpis, showOn, rows]);

  const showCurve = !!(payoutCurve && payoutCurve.on && driveMetric);

  let content;
  if (!rows.length) {
    content = body;
  } else if (!showCurve) {
    content = <div style={{ width: "100%" }}>{body}</div>;
  } else {
    const pl = { dir: "side", swap: false, split: 0.42, ...(pieceLayout || {}) };
    const stack = pl.dir === "stack";
    const pct = Math.round(Math.max(0.25, Math.min(0.75, pl.split)) * 100);
    const box = { border: "1px solid var(--tvd-hair, #EEF2F7)", borderRadius: 10, padding: 12, minWidth: 0, minHeight: 0, overflow: "hidden" };
    const metricBox = (
      <div style={{ ...box, background: "var(--tvd-tile, #F8FAFC)", order: pl.swap ? 2 : 1, flex: stack ? "0 0 auto" : `0 0 ${pct}%` }}>
        {body}
      </div>
    );
    const curveBox = (
      <div style={{ ...box, background: "var(--tvd-card, #fff)", order: pl.swap ? 1 : 2, flex: stack ? "0 0 auto" : "1 1 auto" }}>
        <PayoutCurveBand metric={driveMetric} curve={payoutCurve} gateKpis={gateKpis} gateWorkspaceIds={gateWorkspaceIds} />
      </div>
    );
    content = (
      <div style={{ display: "flex", flexDirection: stack ? "column" : "row", gap: 12, width: "100%", alignItems: "stretch" }}>
        {metricBox}
        {curveBox}
      </div>
    );
  }

  return (
    <SoftBox sx={{ ...CARD_ROOT, opacity: refreshing ? 0.72 : 1, transition: "opacity 0.2s ease", pointerEvents: refreshing ? "none" : undefined }}>
      {/* Natural-height interior — the board measures this to size the row, and
          stretches the card (not the interior) to the answer. */}
      <div data-hug-content style={{ flex: "0 0 auto", display: "flex", flexDirection: "column", minHeight: 0 }}>
      {/* The house header — same 56px strip and hairline divider as every other
          card on the board. This card used to draw its own 14px title inline. */}
      <CardHeader
        title={heading}
        right={headerRight}
      />
      {/* Content sits UNDER the title, never floated in the middle of the card —
          a centred body left air above and below whenever the card was taller
          than what the admin actually put in it. Only the "nothing picked yet"
          placeholder centres, because that IS the whole card. */}
      <SoftBox
        sx={{
          flex: "0 0 auto",
          display: "flex",
          alignItems: rows.length ? "flex-start" : "center",
          justifyContent: "center",
          minHeight: rows.length ? 0 : 96,
          padding: CARD_BODY_PAD,
        }}
      >
        {content}
      </SoftBox>
      </div>
    </SoftBox>
  );
};

export default CustomWidget;
